import { useMemo } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend
} from "recharts";
import { Box, Typography, Paper, Divider } from "@mui/material"; 
import dayjs from "dayjs";
import { formatKpiValue, getKpiConfig } from "../../../../shared/utils/prediction-utils";

const getApiKey = (metric: string, type: 'baseline' | 'adjusted') => {
    const suffix = metric.toLowerCase() === 'occupancy' ? 'occ' : metric.toLowerCase();
    return `${type}_${suffix}`;
};

const formatXAxis = (val: string) => {
    if (!val) return "";
    const d = dayjs(val);
    if (d.isValid()) {
        if (val.includes("-") && val.length > 7) return d.format("MMM D");
        return d.format("MMM 'YY");
    }
    return val;
};

const CustomTooltip = ({ active, payload, label, kpi }: any) => {
    if (active && payload && payload.length) {
        const baselineEntry = payload.find((p: any) => p.dataKey === "baseline");
        const adjustedEntry = payload.find((p: any) => p.dataKey === "adjusted");

        const baseline = baselineEntry ? Number(baselineEntry.value) : 0;
        const adjusted = adjustedEntry ? Number(adjustedEntry.value) : 0;

        const originalData = baselineEntry?.payload || adjustedEntry?.payload || {};
        const isPrediction = originalData.is_prediction;
        const baseConf = originalData.baseline_confidence_pct;
        const scenConf = originalData.scenario_confidence_pct;

        return (
            <Paper sx={{ p: 1.5, border: "1px solid #ccc", minWidth: 200, zIndex: 10, boxShadow: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {label}
                    <Box
                        component="span"
                        sx={{
                            fontSize: '0.65rem',
                            px: 0.8,
                            py: 0.2,
                            borderRadius: 1,
                            bgcolor: isPrediction ? '#e3f2fd' : '#f5f5f5',
                            color: isPrediction ? '#1976d2' : '#666',
                            fontWeight: 'bold'
                        }}
                    >
                        {isPrediction ? "FORECAST" : "ACTUAL"}
                    </Box>
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <span style={{ color: '#9e9e9e', fontSize: '0.8rem' }}>Baseline:</span>
                    <span style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{formatKpiValue(baseline, kpi)}</span>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: isPrediction ? 1 : 0 }}>
                    <span style={{ color: '#1976d2', fontSize: '0.8rem' }}>Adjusted:</span>
                    <span style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#1976d2' }}>{formatKpiValue(adjusted, kpi)}</span>
                </Box>

                {isPrediction && (baseConf !== undefined || scenConf !== undefined) && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #eee' }}>
                        {baseConf !== undefined && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Base Conf:</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>{baseConf}%</Typography>
                            </Box>
                        )}
                        {scenConf !== undefined && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">Scen Conf:</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1976d2' }}>{scenConf}%</Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Paper>
        );
    }
    return null;
};

const METRICS_TO_CHECK = ['adr', 'occupancy', 'revpar'];

export const WhatIfChart = ({ data }: { data: any }) => {

    const chartDataArray = useMemo(() => {
        if (!data) return [];

        const isValid = (arr: any) => {
            return Array.isArray(arr) && arr.length > 0 &&
                Object.keys(arr[0]).some(k => k.startsWith('baseline_'));
        };

        if (data?.results?.multi_kpi?.predictions && isValid(data.results.multi_kpi.predictions)) {
            return data.results.multi_kpi.predictions;
        }

        if (data?.results) {
            for (const metric of METRICS_TO_CHECK) {
                if (isValid(data.results[metric]?.predictions)) {
                    return data.results[metric].predictions;
                }
            }
        }

        if (data?.multi_kpi?.predictions && isValid(data.multi_kpi.predictions)) {
            return data.multi_kpi.predictions;
        }

        if (data?.results) {
            const keys = Object.keys(data.results);
            for (const key of keys) {
                const propData = data.results[key];
                if (isValid(propData?.results?.multi_kpi?.predictions)) {
                    return propData.results.multi_kpi.predictions;
                }
                for (const metric of METRICS_TO_CHECK) {
                    if (isValid(propData?.results?.[metric]?.predictions)) {
                        return propData.results[metric].predictions;
                    }
                }
            }
        }

        if (isValid(data)) return data;

        return [];
    }, [data]);

    if (chartDataArray.length === 0) return null;

    const availableMetrics = METRICS_TO_CHECK.filter(metricKey => {
        const testKey = getApiKey(metricKey, 'baseline');
        return chartDataArray[0] && Object.prototype.hasOwnProperty.call(chartDataArray[0], testKey);
    });

    return (
        <Box sx={{ width: "100%", mt: 2 }}>
            {availableMetrics.map((metricKey) => {
                const config = getKpiConfig(metricKey);

                const formattedData = chartDataArray.map((item: any) => ({
                    name: item.month || item.date || 'Period',
                    baseline: Number(item[getApiKey(metricKey, 'baseline')] || 0),
                    adjusted: Number(item[getApiKey(metricKey, 'adjusted')] || 0),
                    is_prediction: item.is_prediction,
                    baseline_confidence_pct: item.baseline_confidence_pct,
                    scenario_confidence_pct: item.scenario_confidence_pct
                }));

                return (
                    <Box key={metricKey} sx={{
                        mb: 4,
                        p: 2,
                        bgcolor: "#fff",
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                        border: "1px solid #f0f0f0"
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333' }}>
                                {config.label} Analysis
                            </Typography>
                            {config.unit && (
                                <Typography variant="caption" sx={{ ml: 0.5, color: '#666' }}>
                                    ({config.unit})
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ height: 260, width: "100%" }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={formattedData}
                                    margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                                    barGap={6}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f5f5f5" />

                                    <XAxis
                                        dataKey="name"
                                        style={{ fontSize: "11px", fill: '#888', fontWeight: 500 }}
                                        tickMargin={10}
                                        axisLine={false}
                                        tickLine={false}
                                        tickFormatter={formatXAxis}
                                    />

                                    <YAxis
                                        style={{ fontSize: "11px", fill: '#888' }}
                                        axisLine={false}
                                        tickLine={false}
                                        tickFormatter={(val) => val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val}
                                        domain={[0, 'auto']}
                                    />

                                    <Tooltip
                                        content={<CustomTooltip kpi={metricKey} />}
                                        cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                                    />

                                    <Legend
                                        verticalAlign="top"
                                        align="right"
                                        wrapperStyle={{ paddingBottom: '10px', fontSize: '12px' }}
                                        iconType="circle"
                                    />

                                    <Bar
                                        name="Baseline"
                                        dataKey="baseline"
                                        fill="#bdbdbd"
                                        barSize={16}
                                        radius={[4, 4, 4, 4]}
                                    />

                                    <Bar
                                        name="Adjusted"
                                        dataKey="adjusted"
                                        fill="#1976d2"
                                        barSize={16}
                                        radius={[4, 4, 4, 4]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};